import { useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import ReactFlow, { Controls, type Node, type NodeTypes } from 'reactflow';
import { useFlow } from 'hooks';
import { handleNodeColor, Path } from 'utils';
import { NodeVariant } from 'types';
import { PrimaryNode, SecondaryNode } from 'components';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';

import { ResultDrawer, ShareDrawer } from './Drawer';
import { initialNodes, initialEdges } from './AnalysisFlow.config';
import * as S from './AnalysisFlow.styled';

export const AnalysisFlow = () => {
  const navigate = useNavigate();
  const [resultsDrawerContent, setResultsDrawerContent] = useState<Node | null>(null);
  const [shareDrawerIsOpen, setShareDrawerIsOpen] = useState(false);
  const { instance, nodes, edges, onNodesChange, onEdgesChange, onConnect } = useFlow(
    initialNodes,
    initialEdges
  );

  const nodeTypes: NodeTypes = useMemo(
    () => ({ [NodeVariant.PRIMARY]: PrimaryNode, [NodeVariant.SECONDARY]: SecondaryNode }),
    []
  );

  const handleClose = () => {
    setShareDrawerIsOpen(false);
    setResultsDrawerContent(null);
    instance.setNodes((nd) => nd.map((node) => ({ ...node, selected: false })));
  };

  return (
    <S.Container>
      <S.ButtonsWrapper>
        <S.Button onClick={() => navigate(Path.HOME)} endIcon={<EditIcon />}>
          edit query
        </S.Button>
        <S.Button
          endIcon={<SendIcon />}
          onClick={() => {
            setShareDrawerIsOpen(true);
          }}
        >
          share
        </S.Button>
      </S.ButtonsWrapper>
      <ReactFlow
        style={{
          height: '100vh',
          overflow: 'visible',
        }}
        nodes={nodes}
        onNodesChange={onNodesChange}
        edges={edges}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        fitView
        onNodeDragStop={() => {
          instance.setNodes((nd) => nd.map((n) => ({ ...n, selected: false })));
        }}
        onNodeClick={(_, node) => {
          instance.setNodes((nd) =>
            nd.map((n) => ({
              ...n,
              selected: n.id === node.id,
            }))
          );
          setResultsDrawerContent(node);
        }}
      >
        <Controls
          showInteractive={false}
          style={{
            left: '200px',
          }}
        />

        <S.StyledMiniMap
          zoomable
          pannable
          position="bottom-left"
          nodeColor={(node: Node) => handleNodeColor(node.data.color)}
        />
      </ReactFlow>
      {resultsDrawerContent && (
        <ResultDrawer
          isOpen={Boolean(resultsDrawerContent)}
          onClose={handleClose}
          content={resultsDrawerContent}
        />
      )}

      {shareDrawerIsOpen && <ShareDrawer isOpen={shareDrawerIsOpen} onClose={handleClose} />}
    </S.Container>
  );
};
